import { useCallback, useEffect, useState } from "react";
import { BadgeCheck, ThumbsUp, Flag, Image as ImageIcon, Loader2, Pencil, X, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Stars, StarInput, RatingDistribution } from "./Stars";

const REPORT_REASONS = ["Fake review", "Spam", "Offensive content", "Personal information",
  "Misleading information", "Wrong product", "Advertising/promotional content", "Other"];

export default function ReviewsSection({ productId }) {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("relevant");
  const [photosOnly, setPhotosOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [elig, setElig] = useState(null);
  const [writing, setWriting] = useState(false);

  const load = useCallback((p = 1) => {
    api.get(`/reviews/public/product/${productId}`, { params: { sort, photos_only: photosOnly, page: p, per: 8 } })
      .then((r) => setData((prev) => p > 1 && prev ? { ...r.data, reviews: [...prev.reviews, ...r.data.reviews] } : r.data))
      .catch(() => setData({ summary: { rating_avg: 0, rating_count: 0, rating_dist: {} }, reviews: [], total: 0, has_more: false }));
  }, [productId, sort, photosOnly]);

  useEffect(() => { setPage(1); load(1); }, [load]);
  useEffect(() => {
    api.get(`/reviews/eligibility`, { params: { product_id: productId } })
      .then((r) => setElig(r.data)).catch(() => setElig(null));
  }, [productId]);

  const [aiSum, setAiSum] = useState(null);
  useEffect(() => {
    api.get(`/reviews/public/product/${productId}/ai-summary`).then((r) => setAiSum(r.data)).catch(() => setAiSum(null));
  }, [productId]);

  const summary = data?.summary || { rating_avg: 0, rating_count: 0, rating_dist: {} };

  return (
    <section className="mx-auto max-w-5xl px-6 pb-16" data-testid="reviews-section">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">Ratings & Reviews</h2>
          {elig?.eligible && !writing && (
            <button onClick={() => setWriting(true)} data-testid="write-review-btn"
              className="rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white">Write a review</button>
          )}
          {elig?.already_reviewed && !writing && (
            <button onClick={() => setWriting(true)} data-testid="edit-my-review-btn"
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600"><Pencil className="h-3.5 w-3.5" /> Edit your review</button>
          )}
        </div>

        {writing && (
          <WriteReview productId={productId} existing={elig?.already_reviewed ? elig?.review : null}
            onClose={() => setWriting(false)} onSaved={() => { setWriting(false); load(1); api.get(`/reviews/eligibility`, { params: { product_id: productId } }).then((r) => setElig(r.data)).catch(() => {}); }} />
        )}

        {/* summary */}
        {summary.rating_count > 0 ? (
          <div className="mt-5 grid gap-6 sm:grid-cols-[auto,1fr] sm:items-center">
            <div className="text-center">
              <div className="text-4xl font-extrabold text-slate-900" data-testid="rating-average">{summary.rating_avg} ★</div>
              <Stars value={summary.rating_avg} size={18} className="mt-1 justify-center" />
              <p className="mt-1 text-xs text-slate-500" data-testid="rating-count">{summary.rating_count} review{summary.rating_count > 1 ? "s" : ""}</p>
            </div>
            <div className="sm:max-w-sm"><RatingDistribution dist={summary.rating_dist} total={summary.rating_count} /></div>
          </div>
        ) : (
          <div className="mt-5 rounded-xl border border-dashed border-slate-300 p-8 text-center" data-testid="reviews-empty">
            <p className="font-semibold text-slate-700">No reviews yet</p>
            <p className="mt-1 text-sm text-slate-500">{elig?.eligible ? "Be the first to review this product." : "Be the first to review this product after your purchase is delivered."}</p>
          </div>
        )}

        {/* AI summary of genuine reviews (advisory, shown only with enough reviews) */}
        {aiSum?.available && (
          <div className="mt-5 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4" data-testid="ai-review-summary">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-indigo-700"><Sparkles className="h-4 w-4" /> What customers say</p>
            {aiSum.summary && <p className="mt-1 text-sm text-slate-600">{aiSum.summary}</p>}
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              {aiSum.strengths?.length > 0 && <div><p className="text-xs font-semibold text-emerald-600">Loved</p><ul className="mt-0.5 list-disc pl-4 text-sm text-slate-600">{aiSum.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
              {aiSum.weaknesses?.length > 0 && <div><p className="text-xs font-semibold text-rose-500">Could be better</p><ul className="mt-0.5 list-disc pl-4 text-sm text-slate-600">{aiSum.weaknesses.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">AI-generated summary of {aiSum.based_on} verified reviews — not a customer review.</p>
          </div>
        )}

        {/* controls */}
        {summary.rating_count > 0 && (
          <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="reviews-sort"
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-600">
              <option value="relevant">Most Relevant</option>
              <option value="recent">Most Recent</option>
              <option value="highest">Highest Rating</option>
              <option value="lowest">Lowest Rating</option>
            </select>
            <button onClick={() => setPhotosOnly((v) => !v)} data-testid="reviews-photos-toggle"
              className={`inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm ${photosOnly ? "border-blue-400 bg-blue-50 text-blue-600" : "border-slate-200 text-slate-600"}`}>
              <ImageIcon className="h-3.5 w-3.5" /> With Photos
            </button>
          </div>
        )}

        {/* list */}
        <div className="mt-4 space-y-4" data-testid="reviews-list">
          {(data?.reviews || []).map((r) => <ReviewCard key={r.review_id} r={r} onChange={() => load(1)} />)}
        </div>
        {data?.has_more && (
          <button onClick={() => { const np = page + 1; setPage(np); load(np); }} data-testid="reviews-load-more"
            className="mt-4 w-full rounded-lg border border-slate-200 py-2 text-sm font-medium text-slate-600 hover:border-blue-300">Load more reviews</button>
        )}
      </div>
    </section>
  );
}

function WriteReview({ productId, existing, onClose, onSaved }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [title, setTitle] = useState(existing?.title || "");
  const [body, setBody] = useState(existing?.body || "");
  const [images, setImages] = useState(existing?.images || []);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const addImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files.slice(0, 4 - images.length)) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/reviews/media", fd);
        setImages((prev) => [...prev, data.url]);
      }
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Could not upload image"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!rating) { toast.error("Please select a star rating"); return; }
    setBusy(true);
    try {
      if (existing) await api.put(`/reviews/${existing.review_id}`, { rating, title, body, images });
      else await api.post("/reviews", { product_id: productId, rating, title, body, images });
      toast.success("Thanks! Your review has been submitted.");
      onSaved();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Could not submit review"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50/40 p-4" data-testid="write-review-form">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-700">{existing ? "Edit your review" : "Rate this product"}</p>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
      </div>
      <div className="mt-2"><StarInput value={rating} onChange={setRating} /></div>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" data-testid="review-title"
        className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400" />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share your experience (optional)" rows={3} data-testid="review-body"
        className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400" />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {images.map((src, i) => (
          <div key={src.slice(-12) + i} className="relative h-14 w-14 overflow-hidden rounded-lg ring-1 ring-slate-200">
            <img src={src} alt="review" className="h-full w-full object-cover" />
            <button onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))} className="absolute right-0 top-0 bg-black/50 p-0.5 text-white"><X className="h-3 w-3" /></button>
          </div>
        ))}
        {images.length < 4 && (
          <label className="flex h-14 w-14 cursor-pointer items-center justify-center rounded-lg border border-dashed border-slate-300 text-slate-400 hover:border-blue-400" data-testid="review-add-photo">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
            <input type="file" accept="image/*" multiple className="hidden" onChange={addImages} />
          </label>
        )}
      </div>
      <button onClick={submit} disabled={busy} data-testid="submit-review-btn"
        className="mt-3 inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {existing ? "Update review" : "Submit review"}
      </button>
      <p className="mt-2 text-[11px] text-slate-400">By posting, you agree to our <a href="/legal/reviews-and-ratings" target="_blank" rel="noreferrer noopener noreferrer" className="text-blue-500 underline">Reviews &amp; Ratings Policy</a> and <a href="/legal/privacy-policy" target="_blank" rel="noreferrer" className="text-blue-500 underline">Privacy Policy</a>.</p>
    </div>
  );
}

function ReviewCard({ r, onChange }) {
  const [voted, setVoted] = useState(r.voted_helpful);
  const [count, setCount] = useState(r.helpful_count);
  const [reporting, setReporting] = useState(false);

  const helpful = async () => {
    try { const { data } = await api.post(`/reviews/${r.review_id}/helpful`); setVoted(data.voted_helpful); setCount(data.helpful_count); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Please log in to vote"); }
  };
  const report = async (reason) => {
    try { await api.post(`/reviews/${r.review_id}/report`, { reason }); toast.success("Reported — our team will review it."); setReporting(false); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Please log in to report"); }
  };

  return (
    <div className="rounded-xl border border-slate-100 p-4" data-testid={`review-${r.review_id}`}>
      <div className="flex items-center gap-2">
        <Stars value={r.rating} size={14} />
        <span className="text-sm font-semibold text-slate-800">{r.display_name}</span>
        {r.verified_purchase && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600" data-testid="verified-purchase-badge"><BadgeCheck className="h-3 w-3" /> Verified Purchase</span>}
      </div>
      {r.title && <p className="mt-1.5 text-sm font-semibold text-slate-800">{r.title}</p>}
      {r.body && <p className="mt-1 text-sm text-slate-600">{r.body}</p>}
      {r.images?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {r.images.map((src, i) => <img key={src.slice(-12) + i} src={src} alt="review" className="h-16 w-16 rounded-lg object-cover ring-1 ring-slate-200" />)}
        </div>
      )}
      <p className="mt-1.5 text-xs text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}{r.edited ? " · edited" : ""}</p>
      {r.seller_response && (
        <div className="mt-2 rounded-lg border-l-2 border-blue-300 bg-slate-50 p-2.5" data-testid="seller-response">
          <p className="text-xs font-semibold text-blue-600">Seller response</p>
          <p className="text-sm text-slate-600">{r.seller_response.body}</p>
        </div>
      )}
      <div className="mt-2 flex items-center gap-3 text-xs">
        <button onClick={helpful} data-testid={`helpful-${r.review_id}`} className={`inline-flex items-center gap-1 ${voted ? "text-blue-600" : "text-slate-500"} hover:text-blue-600`}>
          <ThumbsUp className="h-3.5 w-3.5" /> Helpful{count ? ` (${count})` : ""}
        </button>
        <button onClick={() => setReporting((v) => !v)} data-testid={`report-${r.review_id}`} className="inline-flex items-center gap-1 text-slate-400 hover:text-rose-500"><Flag className="h-3.5 w-3.5" /> Report</button>
      </div>
      {reporting && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="report-reasons">
          {REPORT_REASONS.map((reason) => (
            <button key={reason} onClick={() => report(reason)} className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] text-slate-600 hover:border-rose-300 hover:text-rose-600">{reason}</button>
          ))}
        </div>
      )}
    </div>
  );
}
